import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { authAxios, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState({
    business_name: "",
    slug: "",
    reply_to_email: "",
    logo_url: null,
    brand_color: "#0d9488",
    sender_email_preview: "",
    shared_sender_domain: "",
  });
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, message: "" });
  const slugTimer = React.useRef(null);

  useEffect(() => {
    loadBrand();
  }, []);

  const loadBrand = async () => {
    try {
      const res = await authAxios.get("/brand");
      setBrand(res.data);
    } catch (err) {
      toast.error("Failed to load brand");
    } finally {
      setLoading(false);
    }
  };

  const checkSlug = (value) => {
    if (slugTimer.current) clearTimeout(slugTimer.current);
    setSlugStatus({ checking: true, available: null, message: "" });
    slugTimer.current = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/brand/check-slug?slug=${encodeURIComponent(value)}`);
        setSlugStatus({
          checking: false,
          available: res.data.available,
          message: res.data.available ? "Available!" : (res.data.reason || "Taken — try another"),
          cleaned: res.data.cleaned,
        });
      } catch {
        setSlugStatus({ checking: false, available: null, message: "" });
      }
    }, 350);
  };

  const handleSlugChange = (raw) => {
    const cleaned = (raw || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    setBrand({ ...brand, slug: cleaned });
    if (cleaned.length >= 2) checkSlug(cleaned);
    else setSlugStatus({ checking: false, available: null, message: "" });
  };

  const handleSave = async () => {
    if (!brand.business_name?.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await authAxios.put("/brand", {
        business_name: brand.business_name,
        slug: brand.slug || null,
        reply_to_email: brand.reply_to_email || null,
        logo_url: brand.logo_url,
        brand_color: brand.brand_color,
      });
      setBrand(res.data);
      if (res.data.slug_was_changed) {
        toast.warning(
          `"${res.data.requested_slug}" was taken — we saved you as "${res.data.slug}" instead.`,
          { duration: 6000 }
        );
      } else {
        toast.success("Brand saved!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save brand");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><LoadingScreen /></Layout>;

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-3xl" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily: 'Manrope'}}>
            Your Brand
          </h1>
          <p className="text-slate-500 mt-1">This is how your emails appear to recipients</p>
        </div>

        {/* Live Preview Card */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 rounded-2xl mb-6 overflow-hidden">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Live Preview · What recipients see</p>
            <div className="bg-white/5 backdrop-blur rounded-xl p-4 font-mono text-sm">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-slate-400 w-16">From:</span>
                <span className="text-white font-semibold" data-testid="brand-preview-from-name">
                  {brand.business_name || "Your Business"}
                </span>
                <span className="text-slate-400">
                  &lt;{brand.slug || "yourbusiness"}@{brand.shared_sender_domain || "swiftlett.com"}&gt;
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-slate-400 w-16">Reply-To:</span>
                <span className="text-emerald-300" data-testid="brand-preview-reply-to">
                  {brand.reply_to_email || user?.email || "you@example.com"}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              When someone replies to your newsletter, it goes straight to your Reply-To inbox.
            </p>
          </CardContent>
        </Card>

        {/* Brand Card */}
        <Card className="bg-white border-slate-100 rounded-2xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
              <Mail className="w-5 h-5 text-afa-navy" />
              Brand Identity
            </CardTitle>
            <CardDescription>
              Customize how your business appears in every email you send
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Business Name */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Business Name</Label>
              <Input
                data-testid="brand-business-name"
                type="text"
                value={brand.business_name || ""}
                onChange={(e) => setBrand({...brand, business_name: e.target.value})}
                placeholder="e.g., Maine Mineral & Gem Museum"
                className="h-12 rounded-xl"
              />
              <p className="text-xs text-slate-500 mt-2">
                Shown as the sender name in the recipient's inbox — make it the name they'll recognize
              </p>
            </div>

            {/* Sender Slug */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Sender Handle</Label>
              <div className="flex items-center gap-0 rounded-xl border border-slate-200 overflow-hidden h-12 focus-within:ring-2 focus-within:ring-afa-navy">
                <Input
                  data-testid="brand-slug"
                  type="text"
                  value={brand.slug || ""}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="yourbusiness"
                  className="border-0 rounded-none h-full focus-visible:ring-0"
                />
                <span className="bg-slate-50 h-full px-4 flex items-center text-slate-500 font-mono text-sm border-l">
                  @{brand.shared_sender_domain || "swiftlett.com"}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2 min-h-[1.25rem]">
                {slugStatus.checking && <span className="text-xs text-slate-400">Checking…</span>}
                {!slugStatus.checking && slugStatus.available === true && (
                  <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {slugStatus.message}
                  </span>
                )}
                {!slugStatus.checking && slugStatus.available === false && (
                  <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {slugStatus.message}
                  </span>
                )}
                {!slugStatus.checking && slugStatus.available === null && (
                  <span className="text-xs text-slate-500">Letters and numbers only — this is your unique handle</span>
                )}
              </div>
            </div>

            {/* Reply-To Email */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Reply-To Email</Label>
              <Input
                data-testid="brand-reply-to"
                type="email"
                value={brand.reply_to_email || ""}
                onChange={(e) => setBrand({...brand, reply_to_email: e.target.value})}
                placeholder={user?.email || "you@yourbusiness.com"}
                className="h-12 rounded-xl"
              />
              <p className="text-xs text-slate-500 mt-2">
                When recipients hit "Reply", their message goes here. Use whatever email you actually check.
              </p>
            </div>

            {/* Brand Color */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Brand Accent Color</Label>
              <div className="flex items-center gap-3">
                <input
                  data-testid="brand-color"
                  type="color"
                  value={brand.brand_color || "#0d9488"}
                  onChange={(e) => setBrand({...brand, brand_color: e.target.value})}
                  className="h-12 w-16 rounded-xl border border-slate-200 cursor-pointer"
                />
                <Input
                  type="text"
                  value={brand.brand_color || ""}
                  onChange={(e) => setBrand({...brand, brand_color: e.target.value})}
                  placeholder="#0d9488"
                  className="h-12 rounded-xl font-mono"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Used as the default accent on new campaigns (you can override per-campaign)
              </p>
            </div>

            {/* Save */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                data-testid="brand-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-afa-navy hover:bg-afa-navy-dark rounded-full font-semibold"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save Brand
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-emerald-900 mb-2">No DNS setup. No fuss.</h3>
                <p className="text-emerald-800 text-sm">
                  We handle all the technical email infrastructure for you — domain verification, SPF, DKIM,
                  the works. You just focus on your business. Your emails are delivered through our
                  dedicated mail engine, <span className="font-semibold">Swiftlett</span>
                  {" "}(<span className="font-mono">{brand.shared_sender_domain}</span>),
                  branded with your name, and replies go straight to your inbox.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Home Route — root path always shows the Suite (the brand front door).
// Logged-in users see a small "Welcome back" strip at the top of Suite with a Dashboard link.

export default SettingsPage;
